// src/components/delivery/DeliveryOrderCard.jsx
import React, { useState } from "react";
import { Phone, MapPin, Package, Calendar, ArrowRight, CheckCircle } from "react-feather";
import { getStatusColor, getStatusLabel, getNextStatus, canUpdateStatus, formatDate } from "../../utils/deliveryHelpers";
import config from "../../config";

const DeliveryOrderCard = ({ order, onUpdateStatus, onViewDetails }) => {
  const [updating, setUpdating] = useState(false);

  const handleStatusUpdate = async () => {
    const nextStatus = getNextStatus(order.deliveryStatus);
    if (!nextStatus) return;

    const confirmed = window.confirm(
      `Update order status to ${getStatusLabel(nextStatus)}?`
    );
    if (!confirmed) return;

    setUpdating(true);
    try {
      await onUpdateStatus(order.orderId, nextStatus, order.deliveryStatus);
    } finally {
      setUpdating(false);
    }
  };

  const handleCallCustomer = () => {
    window.location.href = `tel:${order.customerPhone}`;
  };

  const handleOpenMaps = () => {
    const address = encodeURIComponent(
      `${order.deliveryAddress}, ${order.city}, ${order.pincode}`
    );
    window.open(`https://www.google.com/maps/search/?api=1&query=${address}`, "_blank");
  };

  const nextStatus = getNextStatus(order.deliveryStatus);
  const canUpdate = canUpdateStatus(order.deliveryStatus);

  return (
    <div className="bg-white rounded-xl shadow-sm hover:shadow-lg transition-all duration-300 p-4 md:p-5 border border-gray-100 hover:border-primary/20">
      <div className="flex flex-col sm:flex-row gap-4">
        {/* Book Image */}
        {order.bookCoverImage && (
          <div className="flex-shrink-0 mx-auto sm:mx-0">
            <img
              src={
              order.bookCoverImage && order.bookCoverImage !== "defaultImage.jpg"
                ? `${config.url}${order.bookCoverImage}`
                : "/images/defaultBook.png"
            }
              alt={order.bookName}
              className="w-24 h-32 sm:w-20 sm:h-28 object-cover rounded-lg shadow-md"
              loading="lazy"
              onError={(e) => {
                e.target.src = "/images/defaultBook.png";
              }}
            />
          </div>
        )}

        {/* Order Details */}
        <div className="flex-1 min-w-0">
          {/* Header with Status */}
          <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-2 mb-3">
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-1">
                <Package size={14} className="text-gray-400 flex-shrink-0" />
                <span className="text-xs font-medium text-gray-500">Order #{order.orderId}</span>
              </div>
              <h3 className="font-bold text-gray-900 text-base sm:text-lg line-clamp-1">
                {order.bookName}
              </h3>
              <p className="text-sm text-gray-600 mt-0.5">{order.bookAuthor}</p>
            </div>
            <span
              className={`px-3 py-1.5 rounded-full text-xs font-semibold whitespace-nowrap self-start ${getStatusColor(
                order.deliveryStatus
              )}`}
            >
              {getStatusLabel(order.deliveryStatus)}
            </span>
          </div>

          {/* Customer & Address Info */}
          <div className="bg-gray-50 rounded-lg p-3 mb-3 space-y-2">
            <div className="flex items-start gap-2">
              <div className="w-8 h-8 bg-primary/10 rounded-full flex items-center justify-center flex-shrink-0">
                <span className="text-primary font-semibold text-sm">
                  {order.customerName.charAt(0)}
                </span>
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-gray-900 text-sm">{order.customerName}</p>
                <p className="text-xs text-gray-600">{order.customerPhone}</p>
              </div>
            </div>
            
            <div className="flex items-start text-sm text-gray-700 pt-2 border-t border-gray-200">
              <MapPin size={14} className="mr-2 text-primary flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="text-xs leading-relaxed">{order.deliveryAddress}</p>
                <p className="text-xs font-medium text-gray-900 mt-1">
                  {order.city} - <span className="text-primary">{order.pincode}</span>
                </p>
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-gray-500 pt-2 border-t border-gray-200">
              <div className="flex items-center">
                <Calendar size={14} className="mr-1.5 text-gray-400" />
                <span>Ordered: {formatDate(order.orderDate)}</span>
              </div>
              {(order.deliveryStatus === "BOOK_WITH_ME" || order.deliveredDate) && (
                <div className="flex items-center text-green-700 font-medium">
                  <CheckCircle size={14} className="mr-1.5 text-green-600" />
                  <span>Delivered: {formatDate(order.deliveredDate || order.orderDate)}</span>
                </div>
              )}
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex flex-wrap gap-2">
            <button
              onClick={handleCallCustomer}
              className="flex-1 sm:flex-none flex items-center justify-center space-x-1.5 px-4 py-2.5 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-all shadow-sm hover:shadow text-sm font-medium"
            >
              <Phone size={16} />
              <span>Call</span>
            </button>

            <button
              onClick={handleOpenMaps}
              className="flex-1 sm:flex-none flex items-center justify-center space-x-1.5 px-4 py-2.5 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-all shadow-sm hover:shadow text-sm font-medium"
            >
              <MapPin size={16} />
              <span>Navigate</span>
            </button>

            {canUpdate && nextStatus && (
              <button
                onClick={handleStatusUpdate}
                disabled={updating}
                className="flex-1 sm:flex-auto flex items-center justify-center space-x-1.5 px-4 py-2.5 bg-primary text-white rounded-lg hover:bg-orange-600 transition-all shadow-sm hover:shadow text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {updating ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                    <span>Updating...</span>
                  </>
                ) : (
                  <>
                    <span>Mark as {getStatusLabel(nextStatus)}</span>
                    <ArrowRight size={16} />
                  </>
                )}
              </button>
            )}

            <button
              onClick={() => onViewDetails(order)}
              className="flex items-center justify-center space-x-1.5 px-4 py-2.5 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-all text-sm font-medium"
            >
              <span>View Details</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DeliveryOrderCard;
